import React, { useEffect, useState } from "react";
import { Modal, ModalBody, ModalContent, ModalHeader } from "components/modal";
import ButtonNew from "components/buttonNew/buttonNew";
import { Checkmark, Close } from "assets/icons";
import { toast } from "react-toastify";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { Input, Textarea, Switch } from "@heroui/react";
import { API_URL } from "constants/index";

interface ProductVariantTemplate {
  id: number;
  name: string;
  description?: string;
  ledgerAccountCode: string;
  isActive: boolean;
  indexable: boolean;
  defaultPrice?: number;
  createdAt: string;
  updatedAt: string;
}

interface ProductVariantTemplateModalProps {
  onClose?: () => void;
  isOpen: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  refreshTemplates: () => void;
  editingTemplate?: ProductVariantTemplate | null;
}

const ProductVariantTemplateModal: React.FC<ProductVariantTemplateModalProps> = ({
  onClose,
  isOpen,
  onOpenChange,
  refreshTemplates,
  editingTemplate
}) => {
  const { session } = useSession();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    ledgerAccountCode: '',
    isActive: true,
    indexable: true
  });

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      // If editing, populate form with existing data
      if (editingTemplate) {
        setFormData({
          name: editingTemplate.name,
          description: editingTemplate.description || '',
          ledgerAccountCode: editingTemplate.ledgerAccountCode,
          isActive: editingTemplate.isActive,
          indexable: editingTemplate.indexable
        });
      } else {
        // Reset form for new template
        setFormData({
          name: '',
          description: '',
          ledgerAccountCode: '',
          isActive: true,
          indexable: true
        });
      }
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen, editingTemplate]);

  const handleClose = () => {
    if (isLoading) return; // Prevent closing while loading
    onClose?.();
    onOpenChange?.(false);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.ledgerAccountCode) {
      toast.error('Naam en grootboekrekening zijn vereist');
      return;
    }

    if (isLoading) return; // Prevent multiple submissions

    try {
      setIsLoading(true);
      
      const url = editingTemplate 
        ? `${API_URL}/companies/product-variant-templates/${editingTemplate.id}`
        : `${API_URL}/companies/product-variant-templates`;
      
      const method = editingTemplate ? 'PUT' : 'POST';
      
      const response = await fetchWithAuth(
        url,
        {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        },
        session
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      toast.success(editingTemplate ? 'Productsjabloon bijgewerkt!' : 'Productsjabloon succesvol aangemaakt!');
      refreshTemplates();
      handleClose();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Fout bij opslaan productsjabloon');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal size="4xl" hideCloseButton className="z-100 font-normal" isOpen={isOpen}>
      <ModalContent className="flex h-[80vh] flex-col gap-6 overflow-y-scroll p-6 lg:min-w-[850px]">
        <ModalHeader className="sticky flex w-full items-center justify-between bg-linear-to-b from-white/80 to-white/1 p-0 backdrop-blur-lg">
          <p className="text-lg">
            {editingTemplate ? 'Productsjabloon bewerken' : 'Nieuwe productsjabloon aanmaken'}
          </p>
          <div className="flex items-center gap-2.5">
            <ButtonNew
              variant="primaryMoss"
              onClick={handleSubmit}
              icon={<Checkmark />}
              label="Bewaren"
              isDisabled={isLoading}
            />
            <ButtonNew
              variant="secondaryNeutralMid"
              onClick={handleClose}
              icon={<Close />}
              label="Sluiten"
              isDisabled={isLoading}
            />
          </div>
        </ModalHeader>
        <ModalBody className="flex flex-col gap-6 p-0">
          <section className="flex flex-col gap-3.5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Naam"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                disabled={isLoading}
                classNames={{
                  input: "text-neutral-primary placeholder:text-neutral-secondary-mid",
                  inputWrapper: "p-2 min-h-fit h-fit rounded-sm bg-white shadow-form-element data-[hover=true]:bg-white group-data-[focus=true]:bg-white data-[hover=true]:shadow-form-element-hv group-data-[focus=true]:shadow-form-element-fc",
                }}
              />
              
              <Input
                label="Grootboekrekening code"
                value={formData.ledgerAccountCode}
                onChange={(e) => setFormData({ ...formData, ledgerAccountCode: e.target.value })}
                disabled={isLoading}
                classNames={{
                  input: "text-neutral-primary placeholder:text-neutral-secondary-mid",
                  inputWrapper: "p-2 min-h-fit h-fit rounded-sm bg-white shadow-form-element data-[hover=true]:bg-white group-data-[focus=true]:bg-white data-[hover=true]:shadow-form-element-hv group-data-[focus=true]:shadow-form-element-fc",
                }}
              />
            </div>

            <Textarea
              label="Beschrijving (optioneel)"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              disabled={isLoading}
              classNames={{
                input: "text-neutral-primary placeholder:text-neutral-secondary-mid",
                inputWrapper: "p-2 min-h-fit rounded-sm bg-white shadow-form-element data-[hover=true]:bg-white group-data-[focus=true]:bg-white data-[hover=true]:shadow-form-element-hv group-data-[focus=true]:shadow-form-element-fc",
              }}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex flex-col space-y-2">
                <label className="text-sm text-neutral-primary font-medium">Opties</label>
                <div className="flex items-center space-x-2">
                  <Switch
                    isSelected={formData.indexable}
                    onValueChange={(value) => setFormData({ ...formData, indexable: value })}
                    isDisabled={isLoading}
                    className="text-neutral-primary"
                  />
                  <label className="text-neutral-primary">Indexeerbaar</label>
                </div>
              </div>

              <div className="flex flex-col space-y-2">
                <label className="text-sm text-neutral-primary font-medium">Status</label>
                <div className="flex items-center space-x-2">
                  <Switch
                    isSelected={formData.isActive}
                    onValueChange={(value) => setFormData({ ...formData, isActive: value })}
                    isDisabled={isLoading}
                    className="text-neutral-primary"
                  />
                  <label className="text-neutral-primary">Actief</label>
                </div>
              </div>
            </div>
          </section>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ProductVariantTemplateModal; 